import React, { useEffect, useMemo, useState } from 'react';
import {
  BlackBackground,
  CloseWrapper,
  SearchWrapper,
  StickyHeader,
  TabsWrapper,
} from './SidewideSearch.styles';
import { SearchField, Tabs, TextButton } from '@oneaudi/unified-web-components';
import useFetchSitewideSearch from '../../../hooks/useFetchSitewideSearch';
import VehicleList from './VehicleList';
import { useSpring, animated } from 'react-spring';
import NoResults from './NoResults';
import Spinner from './LoadingSpinner';
import type { InventoryGroup } from '../../../types';
import type { LocaleServiceV1 } from '../../feature-service-types';

interface SitewideSearchProps {
  setSidewideSearchOpen: (arg: boolean) => void;
  localeService?: LocaleServiceV1;
  sortBy: string;
  showPrice: boolean;
}

const SitewideSearch: React.FC<SitewideSearchProps> = ({
  setSidewideSearchOpen,
  localeService,
  sortBy,
  showPrice,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [isClosing, setIsClosing] = useState(false);

  const { listOfVehicles, warnings, searchVehicles, error, loading } = useFetchSitewideSearch();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  //------------------- Animation ---------------------//
  const slideIn = useSpring({
    from: { transform: 'translateX(100%)' },
    to: { transform: isClosing ? 'translateX(100%)' : 'translateX(0%)' },
    config: { tension: 220, friction: 28 },
    onRest: () => {
      if (isClosing) setSidewideSearchOpen(false);
    },
  });

  const fadeIn = useSpring({
    from: { opacity: 0 },
    to: { opacity: isClosing ? 0 : 1 },
    config: { tension: 220, friction: 28 },
  });
  //-------------------------------------------------//

  const handleClose = () => {
    setIsClosing(true);
  };

  const market = useMemo(() => {
    if (localeService?.countryCode === 'US' || localeService?.countryCode === 'CA')
      return localeService?.countryCode.toLowerCase();
    return 'us';
  }, [localeService?.countryCode]);

  const getDigitalDealerId = (): string | undefined => {
    if (typeof window === 'undefined') return undefined;
    const w = window as unknown as {
      digitalData?: { dealer?: Array<{ dealerInfo?: { dealerID?: string } }> };
    };
    return w.digitalData?.dealer?.[0]?.dealerInfo?.dealerID?.slice(4);
  };

  const handleSearch = () => {
    if (!searchInput.trim()) return;
    const dealerId = getDigitalDealerId();
    void searchVehicles({
      variables: {
        input: {
          searchQuery: searchInput,
          market,
          language: 'en',
          sort: sortBy ?? 'OLDEST_IN_LOT',
          dealerId: dealerId ?? '',
        },
      },
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      (event.target as HTMLElement).blur();
      handleSearch();
    }
  };

  const newVehicles = listOfVehicles?.find((toCheck) => toCheck.key === 'NEW');
  const usedVehicles = listOfVehicles?.find((toCheck) => toCheck.key === 'PREOWNED');
  const cpoVehicles = listOfVehicles?.find((toCheck) => toCheck.key === 'CPO');

  const foundNothing =
    !!warnings?.find((toCheck) => toCheck.code === 'NO_FILTERS_EXTRACTED') ||
    (!newVehicles?.vehicles.length &&
      !usedVehicles?.vehicles.length &&
      !cpoVehicles?.vehicles.length &&
      listOfVehicles);

  useEffect(() => {
    if (newVehicles?.vehicles.length) setActiveTab(0);
    if (!newVehicles?.vehicles.length && usedVehicles?.vehicles.length) setActiveTab(1);
    if (!newVehicles?.vehicles.length && !usedVehicles?.vehicles.length) setActiveTab(2);
  }, [newVehicles, usedVehicles]);

  const isEmptyObject = (listOfVehicles: InventoryGroup | undefined) => {
    if (!listOfVehicles || !listOfVehicles?.vehicles.length) return true;
    return Object.keys(listOfVehicles).length === 0;
  };

  const AnimatedBlackBackground = animated(BlackBackground);
  const AnimatedSearchWrapper = animated(SearchWrapper);

  const TabsWithResults = useMemo(() => {
    return (
      <TabsWrapper>
        <Tabs
          id="sitewide-search-tabs"
          size="md"
          scrollButtons={{
            left: { ariaLabel: 'Scroll left' },
            right: { ariaLabel: 'Scroll right' },
          }}
          items={[
            {
              label: 'New',
              active: activeTab === 0,
              disabled: isEmptyObject(newVehicles),
              onClick: () => {
                setActiveTab(0);
              },
              content: <VehicleList vehiclesData={newVehicles} showPrice={showPrice} type="NEW" />,
            },
            {
              label: 'Pre-owned',
              active: activeTab === 1,
              disabled: isEmptyObject(usedVehicles),
              onClick: () => {
                setActiveTab(1);
              },
              content: (
                <VehicleList vehiclesData={usedVehicles} showPrice={showPrice} type="USED" />
              ),
            },
            {
              label: 'Certified',
              active: activeTab === 2,
              disabled: isEmptyObject(cpoVehicles),
              onClick: () => {
                setActiveTab(2);
              },
              content: <VehicleList vehiclesData={cpoVehicles} showPrice={showPrice} type="CPO" />,
            },
          ]}
          onKeyNavigate={(newIndex) => {
            setActiveTab(newIndex);
          }}
        />
      </TabsWrapper>
    );
  }, [activeTab, cpoVehicles, newVehicles, showPrice, usedVehicles]);

  const RenderContent = useMemo(() => {
    if (foundNothing) return <NoResults />;
    if (!listOfVehicles && !loading && error) return <NoResults isError />;
    if (!listOfVehicles && loading && !error) return <Spinner size={48} />;
    if (listOfVehicles && !loading && !error) return TabsWithResults;
    return null;
  }, [TabsWithResults, error, foundNothing, listOfVehicles, loading]);

  return (
    <>
      <AnimatedBlackBackground style={fadeIn} />
      <AnimatedSearchWrapper style={slideIn}>
        <StickyHeader>
          <CloseWrapper>
            <TextButton
              aria-label="close button"
              type="button"
              variant="tertiary"
              onClick={handleClose}
            >
              Close
            </TextButton>
          </CloseWrapper>
          <SearchField
            className="className"
            clearButton={{
              'aria-label': 'Clear the search query',
              onClick: () => {
                setSearchInput('');
              },
            }}
            data-testid="sitewide-search-search-field"
            hint="Search by make, model, price or mileage. Press Enter to view your search results."
            id="search-field"
            label="Search your vehicle"
            margin={[0, 0, '12px', 0]} // 0 16 12 16 for mobile
            onChange={(e) => {
              setSearchInput(e.target.value);
            }}
            onClick={() => {}}
            onKeyDown={handleKeyDown}
            value={searchInput}
          />
        </StickyHeader>
        {RenderContent}
      </AnimatedSearchWrapper>
    </>
  );
};

export default SitewideSearch;
